import React, { useMemo } from "react";

import { Button, Chip, Stack, Typography } from "@mui/material";
import { usePrompt } from "hooks";
import { useReportsMetaSelector } from "store";

import { FiltersState } from "./ExpectedPayments";
import { FilterAutocompletePrompt } from "./FilterAutocompletePrompt";
import { FilterDatePrompt } from "./FilterDatePrompt";

interface Props {
  state: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  resetFilters: () => void;
  hasFilters: boolean;
}

export const Filters: React.FC<Props> = ({ state, setFilters, resetFilters, hasFilters }) => {
  const openPrompt = usePrompt();

  const { assetClasses: assetClassesOptions, deals: dealsOptions } = useReportsMetaSelector();

  const { assetClasses, deals, date } = state;

  const handleClickAssetClasses = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const newState = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: FilterAutocompletePrompt,
      props: {
        title: "Asset Classes",
        options: assetClassesOptions,
        state: assetClasses
      }
    });

    if (!newState) return;
    setFilters((prev) => ({ ...prev, assetClasses: newState }));
  };

  const handleClickDeals = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const newState = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: FilterAutocompletePrompt,
      props: {
        title: "Deals",
        options: dealsOptions,
        state: deals
      }
    });

    if (!newState) return;
    setFilters((prev) => ({ ...prev, deals: newState }));
  };

  const handleClickDate = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const newDate = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: FilterDatePrompt,
      props: {
        initialState: date
      }
    });

    if (!newDate) return;
    setFilters((prev) => ({ ...prev, date: newDate }));
  };

  const datePreview = useMemo(() => {
    return `${date.from} - ${date.to}`;
  }, [date]);

  return (
    <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center" }}>
      <Typography variant="body1">Filters:</Typography>
      <Chip
        onClick={handleClickAssetClasses}
        size="small"
        label={`Asset Classes ${assetClasses.length ? `(${assetClasses.length})` : ""}`}
        {...(assetClasses.length && { onDelete: () => setFilters((prev) => ({ ...prev, assetClasses: [] })) })}
      />
      <Chip
        size="small"
        label={`Deals ${deals.length ? `(${deals.length})` : ""}`}
        {...(deals.length && { onDelete: () => setFilters((prev) => ({ ...prev, deals: [] })) })}
        onClick={handleClickDeals}
      />
      <Chip
        size="small"
        label={datePreview}
        onClick={handleClickDate}
      />
      {hasFilters && (
        <Button variant="outlined" size="sm" onClick={resetFilters}>
          Clear Filters
        </Button>
      )}
    </Stack>
  );
};
